import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretLeft, CaretRight, X, ImageSquare } from '@phosphor-icons/react';

/**
 * PackageSlideshow — full-screen, PowerPoint-style slideshow of every cart item
 * in a saved package. Each slide shows: large image (or themed placeholder),
 * category chip, item name, qty, and price. Navigated with chevron buttons,
 * keyboard arrows, swipe gestures, or thumbnail clicks. Closed with X/Esc.
 *
 * Resolves the slide image from these fields (in order of preference):
 *    item.image · item.photo · item.photos[0] · item.cover
 * Falls back to a category-themed gradient placeholder when none exist.
 */
const CATEGORY_GRADIENTS = {
  Venue: 'from-purple-400 to-indigo-600',
  Catering: 'from-orange-300 to-red-500',
  Decoration: 'from-pink-300 to-rose-500',
  'Custom Decoration': 'from-fuchsia-400 to-purple-600',
  Photography: 'from-sky-300 to-blue-600',
  Videography: 'from-blue-400 to-indigo-600',
  Flowers: 'from-rose-300 to-pink-500',
  'DJ & Entertainment': 'from-violet-400 to-purple-600',
  'Music & Entertainment': 'from-amber-300 to-orange-500',
  Music: 'from-amber-300 to-orange-500',
  Equipment: 'from-gray-300 to-gray-600',
  'Equipment Rental': 'from-gray-300 to-gray-600',
  'Birthday Decoration': 'from-pink-300 to-fuchsia-500',
  'Return Gifts': 'from-emerald-300 to-teal-500',
  Mandapam: 'from-amber-400 to-yellow-600',
  Professional: 'from-teal-300 to-cyan-600',
};

const getCategoryGradient = (cat) =>
  CATEGORY_GRADIENTS[cat] || 'from-slate-300 to-slate-600';

const resolveImage = (item) => {
  if (!item) return null;
  const candidates = [item.image, item.photo, Array.isArray(item.photos) ? item.photos[0] : null, item.cover];
  for (const c of candidates) {
    if (typeof c === 'string' && c) return c;
  }
  return null;
};

const PackageSlideshow = ({ open, items = [], title = 'Package', onClose }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (open) setIdx(0);
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') setIdx(p => Math.min(p + 1, items.length - 1));
      else if (e.key === 'ArrowLeft') setIdx(p => Math.max(p - 1, 0));
    };
    document.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, items.length, onClose]);

  if (!open || items.length === 0) return null;

  const item = items[idx] || items[0];
  const img = resolveImage(item);
  const gradient = getCategoryGradient(item.category);
  const total = items.reduce((s, it) => s + Number(it.price || 0), 0);

  // Resolve absolute image URLs (vendor uploads come as `/uploads/xxx` relative paths).
  // Defensive: bail to null when the resolved value is anything other than a non-empty
  // string (some legacy cart rows still carry a numeric `avatar` index — the per-row
  // fallback below paints a category-gradient placeholder for those).
  const resolveImgSrc = (src) => {
    if (typeof src !== 'string' || !src) return null;
    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:')) return src;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}${src.startsWith('/') ? '' : '/'}${src}`;
  };
  const imgSrc = resolveImgSrc(img);

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex flex-col"
      data-testid="package-slideshow-root"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>

      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-8 py-4 text-sand-50">
        <div className="flex flex-col min-w-0">
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-sand-50/60">Slideshow</span>
          <span className="font-heading text-xl sm:text-2xl truncate">{title}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm font-mono text-sand-50/80 hidden sm:inline" data-testid="slideshow-counter">
            {idx + 1} / {items.length}
          </span>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-brand-primary font-bold text-sm shadow-lg hover:bg-sand-50 hover:scale-105 transition-all"
            aria-label="Close slideshow"
            data-testid="slideshow-close-btn">
            <X size={18} weight="bold" />
            <span>Close</span>
          </button>
        </div>
      </div>

      {/* Main slide */}
      <div className="flex-1 flex items-stretch justify-between px-2 sm:px-6 gap-2 sm:gap-6 min-h-0 overflow-hidden">
        <button
          onClick={() => setIdx(p => Math.max(p - 1, 0))}
          disabled={idx === 0}
          className="flex-shrink-0 self-center p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-sand-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="slideshow-prev-btn">
          <CaretLeft size={28} weight="bold" />
        </button>

        <div className="flex-1 max-w-3xl mx-auto h-full flex flex-col justify-center min-h-0">
          <div className="bg-white rounded-lg overflow-hidden shadow-2xl border border-white/20 flex flex-col min-h-0 max-h-full"
            data-testid="slideshow-slide">
            {/* Image — height capped so the details panel below is always reachable */}
            <div className={`relative w-full h-[28vh] sm:h-[34vh] flex-shrink-0 bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden`}>
              {imgSrc ? (
                <img src={imgSrc} alt={item.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="text-white/90 flex flex-col items-center gap-3 px-8 text-center">
                  <ImageSquare size={56} weight="duotone" />
                  <span className="text-sm uppercase tracking-[0.25em] font-bold opacity-80">
                    {item.category || 'Item'}
                  </span>
                </div>
              )}
              {/* Category chip overlaid on image */}
              <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase font-bold bg-white/90 text-text-primary px-2.5 py-1 rounded">
                {item.category || 'Item'}
              </span>
            </div>
            <div className="p-5 sm:p-6 space-y-3 overflow-y-auto flex-1 min-h-0">
              <h3 className="font-heading text-2xl sm:text-3xl text-text-primary leading-tight">
                {item.name}
              </h3>
              <div className="flex items-end justify-between gap-4 flex-wrap pt-2">
                <div>
                  {item.qty && item.qty > 1 && (
                    <div className="text-xs tracking-[0.18em] uppercase font-bold text-text-secondary mb-1">
                      Quantity · {item.qty}
                    </div>
                  )}
                  <div className="text-xs tracking-[0.18em] uppercase font-bold text-text-secondary">
                    Price
                  </div>
                </div>
                <div className="font-heading text-3xl sm:text-4xl font-bold text-brand-primary"
                  data-testid="slideshow-price">
                  ₹{Number(item.price || 0).toLocaleString('en-IN')}
                </div>
              </div>

              {/* Package details — full food menu for caterer, sub-package details for venues,
                  service-tier breakdown for photography, etc. Falls back to derived fields
                  (parsed variant, category, unit price, line-item id) when `details` is missing. */}
              {(() => {
                const d = item.details || {};
                const hasMenu = Array.isArray(d.menu) && d.menu.length > 0;
                let specs = Array.isArray(d.specs) ? [...d.specs] : [];

                // Derive fallback specs from the cart item itself so older saved packages
                // (created before per-add enrichment shipped) still show useful info.
                if (specs.length === 0 && !hasMenu) {
                  // Pull anything in parens out of the name as a "Variant" row.
                  const m = (item.name || '').match(/\(([^)]+)\)/);
                  if (m && m[1]) specs.push({ label: 'Selection', value: m[1] });
                  if (item.category) specs.push({ label: 'Category', value: item.category });
                  if (item.qty && Number(item.qty) > 1) {
                    specs.push({ label: 'Quantity', value: `${item.qty}` });
                    const unit = Math.round(Number(item.price || 0) / Number(item.qty));
                    if (unit > 0) specs.push({ label: 'Approx. per unit', value: `₹${unit.toLocaleString('en-IN')}` });
                  }
                  if (item.id) {
                    // Surface the underlying line-item reference for support / debugging.
                    specs.push({ label: 'Reference', value: String(item.id).slice(0, 40) });
                  }
                }
                const hasSpecs = specs.length > 0;
                const hasDesc = typeof d.description === 'string' && d.description.length > 0;

                return (
                  <div className="pt-3 mt-3 border-t border-[#E8E1D5] space-y-3"
                    data-testid="slideshow-details">

                    {/* Catering: per-meal menu cards */}
                    {hasMenu && (
                      <div className="space-y-2">
                        <div className="text-xs tracking-[0.18em] uppercase font-bold text-text-secondary">Food Menu</div>
                        {d.menu.map((meal, mi) => (
                          <div key={`${meal.meal}-${mi}`} className="border border-[#E8E1D5] rounded p-3 bg-sand-50/50">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-sm text-text-primary">{meal.meal}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${meal.type === 'Non-Veg' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                                  {meal.type}
                                </span>
                              </div>
                              <span className="text-xs text-text-secondary">
                                ₹{meal.price_per_plate}/plate × {meal.guests} guests
                              </span>
                            </div>
                            {Array.isArray(meal.items) && meal.items.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {meal.items.map((dish, di) => (
                                  <span key={`${dish}-${di}`}
                                    className="text-[11px] px-2 py-0.5 bg-white border border-[#E8E1D5] rounded text-text-primary">
                                    {dish}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Generic key/value spec rows (real or derived) */}
                    {hasSpecs && (
                      <div className="space-y-1">
                        <div className="text-xs tracking-[0.18em] uppercase font-bold text-text-secondary mb-1">Package Details</div>
                        {specs.map((s, si) => (
                          <div key={si} className="flex items-center justify-between gap-3 text-sm border-b border-[#E8E1D5]/50 pb-1 last:border-0">
                            <span className="text-text-secondary uppercase tracking-wider text-[10px] font-bold flex-shrink-0">{s.label}</span>
                            <span className="font-medium text-text-primary text-right break-all">{s.value}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Free-form description */}
                    {hasDesc && (
                      <p className="text-sm text-text-secondary leading-relaxed">{d.description}</p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <button
          onClick={() => setIdx(p => Math.min(p + 1, items.length - 1))}
          disabled={idx === items.length - 1}
          className="flex-shrink-0 self-center p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-sand-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="slideshow-next-btn">
          <CaretRight size={28} weight="bold" />
        </button>
      </div>

      {/* Thumbnail strip */}
      <div className="px-4 sm:px-8 py-4 border-t border-white/10 bg-black/40">
        <div className="flex items-center justify-between mb-2 text-sand-50/80 text-xs">
          <span className="tracking-[0.2em] uppercase font-bold sm:hidden">{idx + 1} / {items.length}</span>
          <span className="tracking-[0.2em] uppercase font-bold hidden sm:inline">Tap to jump</span>
          <span className="font-mono">Total · ₹{total.toLocaleString('en-IN')}</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="slideshow-thumbs">
          {items.map((it, i) => {
            const itImg = resolveImgSrc(resolveImage(it));
            const itGradient = getCategoryGradient(it.category);
            return (
              <button key={`${it.id}-${i}`}
                onClick={() => setIdx(i)}
                className={`flex-shrink-0 w-20 h-14 rounded overflow-hidden border-2 transition-all ${i === idx ? 'border-brand-primary scale-105' : 'border-white/20 hover:border-white/60'}`}
                data-testid={`slideshow-thumb-${i}`}>
                <div className={`w-full h-full bg-gradient-to-br ${itGradient} flex items-center justify-center`}>
                  {itImg ? (
                    <img src={itImg} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageSquare size={20} className="text-white/80" weight="duotone" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {/* Fixed bottom "Close Slideshow" pill — guaranteed visible exit on every screen size */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 font-bold text-sm shadow-xl transition-colors"
            data-testid="slideshow-close-bottom-btn">
            <X size={16} weight="bold" />
            <span>Close Slideshow</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PackageSlideshow;
